import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Star, X } from "lucide-react";

function formatReviewCount(count) {
  const n = Math.abs(Number(count) || 0);
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} отзыв`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} отзыва`;
  return `${n} отзывов`;
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export default function ReviewsSummary({
  data,
  className = "",
  reviewMaxLines = 4,
  showReviewText = true,
  showReviewCard = true,
  onShowAll,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  onClose,
}) {
  const [index, setIndex] = useState(0);

  const normalized = useMemo(() => {
    const distribution = data?.distribution || {};
    const distributionMap = {
      1: Number(distribution[1] ?? distribution["1"] ?? 0) || 0,
      2: Number(distribution[2] ?? distribution["2"] ?? 0) || 0,
      3: Number(distribution[3] ?? distribution["3"] ?? 0) || 0,
      4: Number(distribution[4] ?? distribution["4"] ?? 0) || 0,
      5: Number(distribution[5] ?? distribution["5"] ?? 0) || 0,
    };

    const totalFromDistribution = Object.values(distributionMap).reduce((sum, value) => sum + value, 0);
    const totalReviews = Number(data?.totalReviews ?? totalFromDistribution) || 0;
    const averageRating = Number(data?.averageRating ?? 0) || 0;
    const recent = Array.isArray(data?.recent) ? data.recent : [];

    return {
      distributionMap,
      totalReviews,
      averageRating,
      recent,
    };
  }, [data]);

  const { distributionMap, totalReviews, averageRating, recent } = normalized;
  const hasRecent = recent.length > 0;
  const safeIndex = hasRecent ? Math.min(index, recent.length - 1) : 0;
  const current = hasRecent ? recent[safeIndex] : null;
  const roundedAverage = Math.max(0, Math.min(5, Math.round(averageRating)));
  const reviewMaxHeightRem = Math.max(2, Number(reviewMaxLines) || 4) * 1.25;

  const goPrev = () => {
    if (!hasRecent) return;
    setIndex((prev) => (prev - 1 + recent.length) % recent.length);
  };

  const goNext = () => {
    if (!hasRecent) return;
    setIndex((prev) => (prev + 1) % recent.length);
  };

  useEffect(() => {
    if (!hasRecent || typeof onLoadMore !== "function") return;
    if (!hasMore || loadingMore) return;
    const threshold = Math.max(0, recent.length - 2);
    if (safeIndex >= threshold) {
      onLoadMore();
    }
  }, [hasMore, hasRecent, loadingMore, onLoadMore, recent.length, safeIndex]);

  const ratingsDesc = [5, 4, 3, 2, 1];

  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${className}`}>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="text-base font-semibold text-slate-900">Отзывы пользователей</div>
          {typeof onClose === "function" && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Скрыть отзывы"
              className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((value) => (
            <Star
              key={value}
              className={`h-6 w-6 ${value <= roundedAverage ? "fill-amber-400 text-amber-500" : "text-slate-300"}`}
            />
          ))}
          <div className="ml-2 text-sm font-medium text-slate-700">
            {averageRating > 0 ? averageRating.toFixed(1) : "—"}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 text-sm font-medium text-slate-700">
          <span>({formatReviewCount(totalReviews)})</span>
          {typeof onShowAll === "function" && (
            <button
              type="button"
              onClick={onShowAll}
              className="text-sm font-semibold text-blue-600 underline underline-offset-2 hover:text-blue-700"
            >
              Смотреть все
            </button>
          )}
        </div>

        <div className="space-y-2">
          {ratingsDesc.map((rating) => {
            const count = distributionMap[rating] || 0;
            const percent = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
            return (
              <div key={rating} className="flex items-center gap-2 text-sm text-slate-700">
                <div className="w-5 text-right font-semibold text-slate-800">{rating}</div>
                <Star className="h-4 w-4 fill-amber-400 text-amber-500" />
                <div className="h-5 flex-1 overflow-hidden rounded-sm border border-slate-200 bg-white">
                  <div
                    className="h-full bg-amber-400"
                    style={{ width: `${percent}%` }}
                    aria-hidden="true"
                  />
                </div>
                <div className="w-10 text-right text-slate-500">({count})</div>
              </div>
            );
          })}
        </div>

        {showReviewCard && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            {current ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <Star
                        key={value}
                        className={`h-5 w-5 ${value <= current.rating ? "fill-amber-400 text-amber-500" : "text-slate-300"}`}
                      />
                    ))}
                  </div>
                  <div className="text-xs font-medium text-slate-500">{formatDate(current.createdAt)}</div>
                </div>

                {showReviewText && (
                  <div
                    className="text-sm leading-5 text-slate-800 overflow-y-auto pr-1"
                    style={{ maxHeight: `${reviewMaxHeightRem}rem` }}
                  >
                    {current.comment}
                  </div>
                )}

                <div className="flex items-center justify-end gap-1 pt-1">
                  <button
                    type="button"
                    onClick={goPrev}
                    disabled={recent.length <= 1}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Предыдущий отзыв"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    disabled={recent.length <= 1}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Следующий отзыв"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-600">Пока нет отзывов. Будьте первым, кто оценит сервис.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
